import { AlertTriangle, OctagonAlert, X } from 'lucide-react'
import { useState } from 'react'
import { NavLink } from 'react-router-dom'

import { useHealthAlerts, type HealthAlert } from '../api/health'
import { de } from '../i18n/de'

/**
 * Operational faults (mailbox down, a carrier API failing, ...) surfaced right under the
 * header so they are impossible to miss, yet never block the app. Dismissal is in-memory
 * only -- on purpose, per product: a reload must show the alert again rather than let a
 * click-away hide a live fault for the rest of the day.
 */
export function AlertBanner({ isAdmin }: { isAdmin: boolean }) {
  const { data, isLoading, isError } = useHealthAlerts()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  if (isLoading || isError || !data) return null

  const visible = data.alerts.filter((alert) => !dismissed.has(alert.key))
  if (visible.length === 0) return null

  const dismiss = (key: string) =>
    setDismissed((prev) => {
      const next = new Set(prev)
      next.add(key)
      return next
    })

  return (
    <div className="border-b border-slate-200 dark:border-slate-800">
      {visible.map((alert) => (
        <AlertRow
          key={alert.key}
          alert={alert}
          contact={data.contact}
          isAdmin={isAdmin}
          onDismiss={() => dismiss(alert.key)}
        />
      ))}
    </div>
  )
}

function AlertRow({
  alert,
  contact,
  isAdmin,
  onDismiss,
}: {
  alert: HealthAlert
  contact: string | null
  isAdmin: boolean
  onDismiss: () => void
}) {
  const isError = alert.severity === 'error'
  const Icon = isError ? OctagonAlert : AlertTriangle

  return (
    <div
      role={isError ? 'alert' : 'status'}
      className={`flex items-start gap-x-3 px-4 py-2.5 text-sm ${
        isError
          ? 'bg-rose-50 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300'
          : 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'
      }`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      {/* One line: the title, and the explanation and whom to tell behind it on a click,
          so a long alert does not push the parcels down a third of a phone screen. */}
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-semibold">{alert.title}</span>
        {alert.detail || contact ? (
          <details>
            <summary className="cursor-pointer list-none underline decoration-dotted underline-offset-2 hover:no-underline">
              {de.alerts.details}
            </summary>
            <p className="mt-1">
              {alert.detail}
              {contact ? <span> {de.alerts.contact(contact)}</span> : null}
            </p>
          </details>
        ) : null}
        {isAdmin ? (
          <>
            <NavLink
              to="/status"
              className="font-medium whitespace-nowrap underline decoration-dotted underline-offset-2 hover:no-underline"
            >
              {de.alerts.openStatus}
            </NavLink>
          </>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={de.alerts.dismiss}
        className="shrink-0 self-start rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}
